<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useClientStore } from '@/stores/client'
import { type DriverConfig, driverConfigsApi } from '@/api/entities/review/driverConfigs'
import { uploadApi } from '@/api/entities/review/upload'

definePage({
  meta: {
    title: '리뷰 업로드 관리',
    action: 'read',
    subject: 'review',
  },
})

const clientStore = useClientStore()
const selectedClient = computed(() => clientStore.selectedClient)

// 상태
const loading = ref(false)
const driverConfigs = ref<DriverConfig[]>([])
const confirmDialog = ref(false)
const confirmMessage = ref('')
const confirmAction = ref<(() => Promise<void>) | null>(null)

// 스낵바
const snackbar = ref(false)
const snackbarMessage = ref('')
const snackbarColor = ref('success')

// 테이블 헤더
const headers = [
  { title: '채널', key: 'driver_name', sortable: true },
  { title: '매핑 상품 수', key: 'target_item_maps_count', sortable: true },
  { title: '작업', key: 'actions', sortable: false, width: '400px' },
]

// 데이터 로드
const loadDriverConfigs = async () => {
  if (!selectedClient.value)
    return

  loading.value = true
  try {
    const { success, data } = await driverConfigsApi.listDriverConfigs(selectedClient.value.id, {
      per_page: 100,
    })

    if (success && data) {
      driverConfigs.value = (data.data || []).map((config: DriverConfig) => ({
        ...config,
        driver_name: config.download_driver?.name || `드라이버 ${config.id}`,
      }))
    }
  }
  catch (error) {
    console.error('Failed to load driver configs:', error)
  }
  finally {
    loading.value = false
  }
}

// 확인 다이얼로그 표시
const showConfirm = (message: string, action: () => Promise<void>) => {
  confirmMessage.value = message
  confirmAction.value = action
  confirmDialog.value = true
}

// 확인 실행
const executeConfirm = async () => {
  if (confirmAction.value)
    await confirmAction.value()

  confirmDialog.value = false
}

// 알림 표시
const showNotification = (message: string, color: string = 'success') => {
  snackbarMessage.value = message
  snackbarColor.value = color
  snackbar.value = true
}

// 모든 채널 샘플 이관
const uploadSampleReviews = () => {
  if (!selectedClient.value)
    return

  showConfirm(
    `${selectedClient.value.name}의 모든 채널 "샘플" 이관을 요청 하시겠습니까?`,
    async () => {
      const { success, data } = await uploadApi.uploadSampleReviewsForClient(selectedClient.value!.id)
      if (success) {
        if (data?.is_reserved)
          showNotification(`${selectedClient.value!.name}의 모든 채널 "샘플" 이관을 요청했습니다.`)
        else
          showNotification('모든 채널의 샘플 또는 전체 이관이 진행 중입니다.', 'warning')
      }
    },
  )
}

// 모든 채널 전체 이관
const uploadReviews = () => {
  if (!selectedClient.value)
    return

  showConfirm(
    `${selectedClient.value.name}의 모든 채널 "전체" 이관을 요청 하시겠습니까?`,
    async () => {
      const { success, data } = await uploadApi.uploadAllReviewsForClient(selectedClient.value!.id)
      if (success) {
        if (data?.is_reserved)
          showNotification(`${selectedClient.value!.name}의 모든 채널 "전체" 이관을 요청했습니다.`)
        else
          showNotification('모든 채널의 샘플 또는 전체 이관이 진행 중입니다.', 'warning')
      }
    },
  )
}

// 드라이버별 샘플 이관
const uploadSampleReviewsForDriverConfig = (driverConfigId: number, driverName: string) => {
  if (!selectedClient.value)
    return

  showConfirm(
    `${selectedClient.value.name}의 ${driverName} "샘플" 이관을 요청 하시겠습니까?`,
    async () => {
      const { success, data } = await uploadApi.uploadSampleReviewsForDriverConfig(
        selectedClient.value!.id,
        driverConfigId,
      )

      if (success) {
        if (data?.is_reserved)
          showNotification(`${selectedClient.value!.name}의 ${driverName} "샘플" 이관을 요청했습니다.`)
        else
          showNotification(`${driverName} 샘플 또는 전체 이관이 진행 중입니다.`, 'warning')
      }
    },
  )
}

// 드라이버별 전체 이관
const uploadReviewsForDriverConfig = (driverConfigId: number, driverName: string) => {
  if (!selectedClient.value)
    return

  showConfirm(
    `${selectedClient.value.name}의 ${driverName} "전체" 이관을 요청 하시겠습니까?`,
    async () => {
      const { success, data } = await uploadApi.uploadAllReviewsForDriverConfig(
        selectedClient.value!.id,
        driverConfigId,
      )

      if (success) {
        if (data?.is_reserved)
          showNotification(`${selectedClient.value!.name}의 ${driverName} "전체" 이관을 요청했습니다.`)
        else
          showNotification(`${driverName} 샘플 또는 전체 이관이 진행 중입니다.`, 'warning')
      }
    },
  )
}

// 실패 이력 삭제
const deleteFailedReviews = () => {
  if (!selectedClient.value)
    return

  showConfirm(
    `${selectedClient.value.name}의 모든 채널 실패 이력을 삭제하시겠습니까?`,
    async () => {
      const { success } = await uploadApi.deleteFailedReviewsForClient(selectedClient.value!.id)
      if (success) {
        showNotification('실패 이력이 삭제되었습니다.')
        loadDriverConfigs()
      }
    },
  )
}

// 전체 이력 삭제
const deleteReviews = () => {
  if (!selectedClient.value)
    return

  showConfirm(
    `${selectedClient.value.name}의 모든 채널 전체 이력을 삭제하시겠습니까?`,
    async () => {
      const { success } = await uploadApi.deleteAllReviewsForClient(selectedClient.value!.id)
      if (success) {
        showNotification('전체 이력이 삭제되었습니다.')
        loadDriverConfigs()
      }
    },
  )
}

// 크리마 리뷰 삭제
const deleteCremaReviewsByClientId = () => {
  if (!selectedClient.value)
    return

  showConfirm(
    `${selectedClient.value.name}의 크리마 전체 리뷰를 삭제하시겠습니까?`,
    async () => {
      const { success } = await uploadApi.deleteCremaReviewsForClient(selectedClient.value!.id)
      if (success)
        showNotification('크리마 리뷰가 삭제되었습니다.')
    },
  )
}

// 카페24 리뷰 삭제
const deleteCafe24ReviewsByClientId = () => {
  if (!selectedClient.value)
    return

  showConfirm(
    `${selectedClient.value.name}의 카페24 전체 리뷰를 삭제하시겠습니까?`,
    async () => {
      const { success } = await uploadApi.deleteCafe24ReviewsForClient(selectedClient.value!.id)
      if (success)
        showNotification('카페24 리뷰가 삭제되었습니다.')
    },
  )
}

// 감시자
watch(selectedClient, () => {
  if (selectedClient.value)
    loadDriverConfigs()
  else
    driverConfigs.value = []
})

onMounted(() => {
  if (selectedClient.value)
    loadDriverConfigs()
})
</script>

<template>
  <div>
    <!-- 고객사 선택 -->
    <VRow class="mb-4">
      <VCol
        cols="12"
        md="4"
      >
        <ClientSelector />
      </VCol>
    </VRow>

    <!-- 메인 카드 -->
    <VCard>
      <VCardText>
        <!-- 액션 버튼 -->
        <VRow class="mb-4">
          <VCol>
            <div class="d-flex gap-2">
              <VBtn
                variant="outlined"
                :disabled="!selectedClient"
                @click="uploadSampleReviews"
              >
                모든 채널 샘플 이관
              </VBtn>
              <VBtn
                color="primary"
                :disabled="!selectedClient"
                @click="uploadReviews"
              >
                모든 채널 전체 이관
              </VBtn>
            </div>
          </VCol>
          <VCol class="d-flex justify-end gap-2">
            <!-- 크리마/카페24 삭제 버튼 -->
            <VBtn
              v-if="selectedClient && selectedClient.review_service === 'crema'"
              color="warning"
              @click="deleteCremaReviewsByClientId"
            >
              크리마 전체 리뷰 삭제
            </VBtn>
            <VBtn
              v-else-if="selectedClient && selectedClient.review_service === 'cafe24_api'"
              color="warning"
              @click="deleteCafe24ReviewsByClientId"
            >
              카페24 전체 리뷰 삭제
            </VBtn>

            <VBtn
              color="error"
              :disabled="!selectedClient"
              @click="deleteFailedReviews"
            >
              모든 채널 실패 이력 삭제
            </VBtn>
            <VBtn
              variant="outlined"
              color="error"
              :disabled="!selectedClient"
              @click="deleteReviews"
            >
              모든 채널 전체 이력 삭제
            </VBtn>
          </VCol>
        </VRow>

        <!-- 데이터 테이블 -->
        <VDataTable
          v-if="selectedClient"
          :headers="headers"
          :items="driverConfigs"
          :loading="loading"
          class="elevation-0"
        >
          <template #item.actions="{ item }">
            <div class="d-flex gap-2">
              <!-- 크리마/카페24 리뷰 삭제 -->
              <VBtn
                v-if="selectedClient && selectedClient.review_service === 'crema'"
                color="warning"
                size="small"
              >
                크리마 리뷰 삭제
              </VBtn>
              <VBtn
                v-if="selectedClient && selectedClient.review_service === 'cafe24_api'"
                color="warning"
                size="small"
              >
                카페24 리뷰 삭제
              </VBtn>

              <VBtn
                variant="outlined"
                size="small"
                @click="uploadSampleReviewsForDriverConfig(item.id, item.driver_name)"
              >
                샘플 이관
              </VBtn>
              <VBtn
                color="primary"
                size="small"
                @click="uploadReviewsForDriverConfig(item.id, item.driver_name)"
              >
                전체 이관
              </VBtn>
              <VBtn
                variant="outlined"
                color="info"
                size="small"
              >
                위젯 이관
              </VBtn>
              <VBtn
                color="error"
                size="small"
              >
                실패 이력 삭제
              </VBtn>
              <VBtn
                variant="outlined"
                color="error"
                size="small"
              >
                전체 이력 삭제
              </VBtn>
            </div>
          </template>
        </VDataTable>

        <p
          v-else
          class="text-center py-8"
        >
          고객사를 선택해주세요.
        </p>
      </VCardText>
    </VCard>

    <!-- 확인 다이얼로그 -->
    <VDialog
      v-model="confirmDialog"
      max-width="500"
    >
      <VCard>
        <VCardTitle>확인</VCardTitle>
        <VCardText>{{ confirmMessage }}</VCardText>
        <VCardActions>
          <VSpacer />
          <VBtn
            variant="text"
            @click="confirmDialog = false"
          >
            취소
          </VBtn>
          <VBtn
            color="primary"
            @click="executeConfirm"
          >
            확인
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <!-- 스낵바 -->
    <VSnackbar
      v-model="snackbar"
      :color="snackbarColor"
      :timeout="3000"
    >
      {{ snackbarMessage }}
    </VSnackbar>
  </div>
</template>
